import { ResourceNotAvailablePlaceholder } from "@/app/components/cards/ResourceNotAvailablePlaceholder";
import { getSubjectColorIndex } from "@/lib/strapi/formatters";
import { Carousel } from "@mantine/carousel";
import { Box, Stack } from "@mantine/core";
import { Flashcard } from "@tsTypes/flash-schema";
import { shuffleArray } from "@utils/helpers";
import clsx from "clsx";
import type { EmblaCarouselType } from "embla-carousel";
import { useMemo, useState } from "react";
import {
  deckReviewingAction,
  deckShuffleAction,
  deckWipeReviewCardsAction,
  selectDeckById,
} from "../../../../../../../../../features/flashcards/subjectFlashcardsSlice";
import {
  useCustomDispatch,
  useCustomSelector,
} from "../../../../../../../../../lib/redux/hooks";
import { default as DeckButtons } from "./buttons/DeckButtons";
import { default as FlashCard, useFlashcardStyles } from "./FlashCard";
import classes from "./FlashCardDeck.module.css";
import FlashCardReviewCard from "./FlashCardReviewCard";
import Indicators from "./Indicators";

type FlashCardDeckProps = {
  flashcards: Flashcard[] | undefined;
  subjectNameLower: string;
  subjectDocumentId: string;
  topicDocumentId: string;
  currentSubTopicDocumentId: string | string[];
  disableActions: boolean;
};

export default function FlashCardDeck({
  flashcards,
  subjectNameLower,
  subjectDocumentId,
  topicDocumentId,
  currentSubTopicDocumentId,
  disableActions,
}: FlashCardDeckProps) {
  const subjectColorIndex = getSubjectColorIndex(subjectNameLower);
  const dispatch = useCustomDispatch();
  const { classes: flashcardClasses, style: flashcardStyle } =
    useFlashcardStyles({
      subjectColorIndex,
      front: true, // placeholder for flashcard styles since we don't use classes this depends on (State sits level down)
    });
  const [embla, setEmbla] = useState<EmblaCarouselType | null>(null);
  const [active, setActive] = useState<number>(1);
  const [attempted, setAttempted] = useState<string[]>([]);
  // Store flashcard attempts under decks, which can be subtopic or topic level, so first we generate an ID based on the current subtopic or topic
  const deckDocumentId =
    typeof currentSubTopicDocumentId === "string"
      ? currentSubTopicDocumentId
      : topicDocumentId;
  const flashCardReduxMetadata = useCustomSelector((state) =>
    selectDeckById(state, subjectDocumentId, deckDocumentId)
  ) ?? {
    reviewing: false, // force false on first render
    reviewCards: [], // force blank on first render
    shuffled: false, // force false on first render
    reversed: false, // force false on first render
  };
  const relevantCards = useMemo(() => {
    const cardsToReview = flashCardReduxMetadata.reviewing
      ? flashcards?.filter((cards) =>
          flashCardReduxMetadata.reviewCards.includes(cards.documentId)
        ) ?? []
      : flashcards ?? [];
    return flashCardReduxMetadata.shuffled
      ? (shuffleArray(cardsToReview) as Flashcard[])
      : cardsToReview;
  }, [flashCardReduxMetadata]);

  const flashcardCards = useMemo(() => {
    let pageCount = 0;
    if (!flashcards) {
      return getFlashcardPlaceholer(
        subjectNameLower + topicDocumentId + currentSubTopicDocumentId,
        "in development"
      );
    }
    return [
      ...relevantCards.map((flashcard) => {
        pageCount++;
        if (pageCount > 5 && disableActions) {
          return getFlashcardPlaceholer(flashcard.documentId, "locked");
        }
        return (
          <FlashCard
            key={flashcard.documentId}
            deckDocumentId={deckDocumentId}
            reviewCards={flashCardReduxMetadata.reviewCards}
            flashcard={flashcard}
            subjectNameLower={subjectNameLower}
            topicDocumentId={topicDocumentId}
            subjectDocumentId={subjectDocumentId}
            currentSubTopicDocumentId={currentSubTopicDocumentId}
            reversed={flashCardReduxMetadata.reversed}
            setAttempted={setAttempted}
            disableActions={disableActions}
          />
        );
      }),
      // review card at end of deck
      <FlashCardReviewCard
        key={
          subjectNameLower +
          `${topicDocumentId ?? ""}${currentSubTopicDocumentId ?? ""}review`
        }
        subjectColorIndex={subjectColorIndex}
        reviewCardsCount={flashCardReduxMetadata.reviewCards.length}
        setIsReviewingHandler={setIsReviewingHandler}
      />,
    ];
  }, [
    subjectColorIndex,
    deckDocumentId,
    flashCardReduxMetadata,
    relevantCards,
    subjectNameLower,
    topicDocumentId,
    subjectDocumentId,
    currentSubTopicDocumentId,
    disableActions,
  ]);

  function setShuffledHandler() {
    dispatch(
      deckShuffleAction({
        subjectDocumentId,
        deckDocumentId,
      })
    );
    embla?.scrollTo(0);
  }
  function setIsReviewingHandler(reset?: true) {
    // if reset click
    if (reset) {
      flashCardReduxMetadata.reviewing &&
        dispatch(
          deckReviewingAction({
            subjectDocumentId,
            deckDocumentId,
            setReviewing: false,
          })
        );
      dispatch(
        deckWipeReviewCardsAction({
          subjectDocumentId,
          deckDocumentId,
        })
      );
    }
    // if review click and not reviewing, change to review
    // (fires even if already reviewing to trigger re-render of memo above for review cards to decrease on re-review)
    else if (!flashCardReduxMetadata.reviewing) {
      dispatch(
        deckReviewingAction({
          subjectDocumentId,
          deckDocumentId,
          setReviewing: true,
        })
      );
    }
    setAttempted([]);
    embla?.scrollTo(0);
  }

  function getFlashcardPlaceholer(
    key: string,
    reason: "locked" | "in development"
  ) {
    return (
      <Carousel.Slide
        className={flashcardClasses.card}
        key={key}
        style={{ overflow: "hidden", ...flashcardStyle }}
      >
        <Stack
          className={clsx(flashcardClasses.cardSide, flashcardClasses.front)}
          gap={0}
        >
          <ResourceNotAvailablePlaceholder
            reason={reason}
            resource="flashcards"
            subjectColorIndex={subjectColorIndex}
          />
        </Stack>
      </Carousel.Slide>
    );
  }

  return (
    <Box mx={"auto"} maw={"min(750px, 100%)"} w={"100%"}>
      <Indicators
        attemptedTally={attempted.length}
        isReviewing={flashCardReduxMetadata.reviewing}
        reviewCardsLength={flashCardReduxMetadata.reviewCards.length}
      />
      <Carousel
        my={"lg"}
        onSlideChange={(value) => {
          setActive(value + 1);
        }}
        getEmblaApi={setEmbla}
        className={classes.customBtnStyles}
      >
        {flashcardCards}
      </Carousel>
      <DeckButtons
        subjectDocumentId={subjectDocumentId}
        deckDocumentId={deckDocumentId}
        shuffled={flashCardReduxMetadata.shuffled}
        reversed={flashCardReduxMetadata.reversed}
        active={active}
        embla={embla}
        flashcardTally={relevantCards.length}
        setShuffledHandler={setShuffledHandler}
      />
    </Box>
  );
}
